<template>
  <div class="container-fluid battery">
    <page-title
      :isDataLoading="isLoading"
      :title="
        formControl.data_value == 'level_points' ? $t('battery.levels') : $t('battery.temperatures')
      "
      :iconClasses="
        formControl.data_value == 'level_points' ? 'fas fa-battery-half' : 'fas fa-thermometer-half'
      "
      :canDownload="false"
      :isTimelineDisplayed="false"
    />

    <div class="container-fluid">
      <div class="row justify-content-md-center mb-4">
        <form-controls
          v-if="controlsConfig.length > 0"
          :controls="controlsConfig"
          v-on:updateControl="handleChange"
          v-on:setDefaultValues="setFormControlsDefault"
        />
      </div>
      <div class="row mt-2">
        <div class="container col-sm-12">
          <ul class="nav nav-tabs justify-content-center" id="myTab" role="tablist">
            <li class="nav-item">
              <a
                class="nav-link active"
                id="time-tab"
                data-toggle="tab"
                href="#time"
                role="tab"
                aria-controls="time"
                aria-selected="true"
                >{{
                  formControl.data_value == 'level_points'
                    ? $t('battery.levels')
                    : $t('battery.temperatures')
                }}</a
              >
            </li>
          </ul>
          <div class="tab-content mb-3" id="myTabContent">
            <div
              class="tab-pane fade show active pb-1"
              id="time"
              role="tabpanel"
              aria-labelledby="time-tab"
            >
              <div v-if="downloaded">
                <battery-exploitation-chart
                  :dataset="dataset"
                  :site="formControl.site"
                  :chartId="chartId"
                  :xTitle="timeTitle"
                  :yTitle="batteryLevelTitle"
                  @update="onChartDataUpdate"
                ></battery-exploitation-chart>
                <chart-config-menu
                  :chart="dataset"
                  :chartsData="chartsData"
                  @showModal="() => {}"
                ></chart-config-menu>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import datePicker from 'vue-bootstrap-datetimepicker'
import { utilsMixin, chartMixin, sitesMixin } from '../utils/mixins'
import moment from 'moment'

export default {
  name: 'BatteryLevel',
  dependencies: ['technicalService'],
  mixins: [utilsMixin, chartMixin, sitesMixin],
  data() {
    return {
      datePicker: datePicker,
      config: {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$locale,
      },
      downloaded: false,
      isLoading: false,
      dataset: {},
      chartId: 'batteriesChart',
      lastBatteryLevelChartId: 'lastBatteryLevelChart',
      firstBatteryLevelChartId: 'firstBatteryLevelChart',
      batteryGradientsChartId: 'batteryGradientsChartId',
      batteryLevelEndTitle: 'Number of sessions over battery level at the end of visit',
      batteryLevelStartTitle: 'Number of sessions over battery level at the start of visit',
      timeTitle: this.$t('filter.duration') + ' (minutes)',
      batteryLevelTitle: this.$t('battery.levels') + ' (%)',
      sessionTitle: 'Sessions (nb)',
      chartsData: {},
      rotationsDataset: {},
      lastBatteryValuesDisplayed: false,
      formControl: {},
      controlsConfig: [],
    }
  },
  mounted() {
    if (this.$route.params.date) {
      this.formControl.date = moment(this.$route.params.date, 'YYYY-MM-DD').format(this.dateFormat)
    } else {
      let now = moment(new Date(), 'HH:mm')
      let limit = moment('14:00', 'HH:mm')
      if (now.isBefore(limit)) {
        this.formControl.date = moment(new Date())
          .subtract(1, 'day')
          .format(this.dateFormat)
      } else {
        this.formControl.date = moment(new Date()).format(this.dateFormat)
      }
    }

    this.formControl.site = this.$route.params.site
      ? this.$route.params.site
      : this.getCurrentUser.sites[0].site
    this.formControl.data_value = this.$route.params.value
      ? this.$route.params.value
      : 'level_points'

    this.controlsConfig = this.generateControlsConfig()

    this.fetchBatteryInformations()
  },
  components: {
    'battery-exploitation-chart': () => import('./charts/exploitation/BatteryExploitationChart'),
    'chart-config-menu': () => import('./utils/ChartConfigMenu'),
    'page-title': () => import('./utils/PageTitle'),
    'form-controls': () => import('./utils/FormControls'),
  },
  computed: {
    sites: function() {
      const filtersConfig = this.$store.getters['config/filtersConfig']
      return filtersConfig.filter_values.site
    },
  },
  methods: {
    handleChange(change) {
      this.formControl[change.vModel] = change.value
      this.fetchBatteryInformations()
    },
    onBatteryFormChange: function() {
      this.fetchBatteryInformations()
    },
    fetchBatteryInformations() {
      this.isLoading = true
      let date = moment(this.formControl.date, this.dateFormat).format('YYYY-MM-DD')
      window.history.pushState(
        null,
        null,
        '/battery/informations/' +
          this.formControl.data_value +
          '/site/' +
          this.formControl.site +
          '/date/' +
          date,
      )

      this.technicalService
        .getBatteryInformationsForSiteAndDate(
          this.formControl.site,
          this.formControl.data_value,
          date,
        )
        .then(data => {
          this.downloaded = true
          this.dataset = data.result
          this.isLoading = false
        })
        .catch(e => {
          console.error('Error', e)
          this.isLoading = false
        })
    },
    onChartDataUpdate: function(chartData) {
      if (chartData.type == 'rotation_gradient') {
        this.rotationsDataset = chartData
      }
      this.chartsData[chartData.type] = chartData
    },
    onLastBatteryValuesTabClick: function() {
      setTimeout(() => {
        this.lastBatteryValuesDisplayed = true
      }, 500)
    },
    generateControlsConfig: function() {
      let config = [
        {
          label: this.$t('filter.by_site'),
          type: 'select',
          vModel: 'site',
          default: this.formControl.site ? this.formControl.site : 1,
          options: this.getCurrentUser.sites.map(site => ({
            value: site.site,
            label: site.name,
          })),
        },
        {
          label: this.$t('battery.infos'),
          type: 'select',
          vModel: 'data_value',
          default: this.formControl.data_value ? this.formControl.data_value : 'level_points',
          options: [
            { value: 'level_points', label: this.$t('battery.levels') },
            {
              value: 'temperature_points',
              label: this.$t('battery.temperatures'),
            },
          ],
        },
        {
          label: 'Date',
          type: 'date',
          vModel: 'date',
          default: this.formControl.date,
        },
      ]
      return config
    },
  },
}
</script>

<style scoped>
.starter-template {
  padding: 3rem 1.5rem;
  text-align: center;
}

.table > tbody > tr > td {
  vertical-align: middle;
}

.table > tbody > tr > th {
  vertical-align: middle;
}
.tab-content select {
  background-color: #20343d !important;
}

.chart-config-menu {
  position: absolute;
  bottom: 0.5em !important;
  right: 2.4em;
}
</style>
